"use client";
import React from 'react';
import { motion } from 'framer-motion';

const CLASH = 'var(--font-clash), "Clash Display", sans-serif';
const MANROPE = 'var(--font-manrope), Manrope, sans-serif';
const INK = '#080400';
const PURPLE = '#3730a3';
const LIME = '#ceff1a';
const CARD = '#fbfdfd';

const questions = [
    'Are you attracting the right visitors?',
    'Do they understand the offer?',
    'Do they trust the business?',
    'Is the next step obvious?',
    'Is anything creating unnecessary friction?',
];

function QuestionFlow() {
    return (
        <div className="relative w-full max-w-[480px] mx-auto lg:mx-0">
            <div className="absolute left-[19px] top-6 bottom-6 pointer-events-none" style={{ width: 2, background: 'rgba(55,48,163,0.15)' }} />

            <div className="flex flex-col gap-3 relative">
                {questions.map((q, i) => (
                    <motion.div
                        key={q}
                        initial={{ opacity: 0, x: -14 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.45, delay: 0.2 + i * 0.09, ease: 'easeOut' }}
                        className="flex items-center gap-4 relative z-10"
                    >
                        <span
                            className="w-10 h-10 rounded-full flex items-center justify-center text-xs font-bold shrink-0"
                            style={{ fontFamily: CLASH, background: CARD, border: '2px solid', borderColor: PURPLE, color: PURPLE }}
                        >
                            {String(i + 1).padStart(2, '0')}
                        </span>
                        <div
                            className="flex-1 rounded-2xl px-5 py-4"
                            style={{ background: CARD, border: '1px solid rgba(8,4,0,0.08)', boxShadow: '0 10px 24px -18px rgba(8,4,0,0.2)' }}
                        >
                            <span className="text-sm md:text-base font-bold" style={{ fontFamily: CLASH, color: INK }}>
                                {q}
                            </span>
                        </div>
                    </motion.div>
                ))}

                <motion.div
                    initial={{ opacity: 0, x: -14 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: 0.2 + questions.length * 0.09, ease: 'easeOut' }}
                    className="flex items-center gap-4 relative z-10 mt-1"
                >
                    <span
                        className="w-10 h-10 rounded-full flex items-center justify-center shrink-0"
                        style={{ background: PURPLE }}
                    >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                            <path d="M5 12.5l4.5 4.5L19 7" stroke={LIME} strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                    </span>
                    <div className="flex-1 rounded-2xl px-5 py-4" style={{ background: PURPLE }}>
                        <span className="text-sm md:text-base font-bold leading-snug" style={{ fontFamily: CLASH, color: '#ffffff' }}>
                            We optimise the entire conversion journey — not isolated website elements.
                        </span>
                    </div>
                </motion.div>
            </div>
        </div>
    );
}

export default function CRONotJustButtons() {
    return (
        <section className="relative overflow-hidden pt-20 pb-16 md:pt-28 md:pb-20 px-6 md:px-16 lg:px-24" style={{ background: '#ffffff', borderTopLeftRadius: '80px', borderTopRightRadius: '80px' }}>
            <div className="absolute inset-0 pointer-events-none opacity-[0.035]" style={{ backgroundImage: 'radial-gradient(circle, rgba(55,48,163,1) 1px, transparent 1px)', backgroundSize: '40px 40px' }} />

            <div className="max-w-[1200px] mx-auto relative z-10 grid grid-cols-1 lg:grid-cols-12 gap-14 lg:gap-10 items-start">
                <div className="lg:col-span-5 flex flex-col gap-6">
                    <motion.span
                        initial={{ opacity: 0, y: 10 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.5 }}
                        className="text-xs font-bold tracking-[0.2em] uppercase"
                        style={{ color: PURPLE, fontFamily: CLASH }}
                    >
                        Beyond Cosmetic Changes
                    </motion.span>

                    <motion.h2
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.6, delay: 0.1 }}
                        className="text-3xl md:text-5xl font-bold tracking-tight leading-tight"
                        style={{ fontFamily: CLASH, color: INK }}
                    >
                        CRO Isn&apos;t Just About Button Colours
                    </motion.h2>

                    <motion.p
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.6, delay: 0.15 }}
                        className="text-base md:text-lg leading-relaxed"
                        style={{ fontFamily: MANROPE, color: 'rgba(8,4,0,0.6)', letterSpacing: '0.02em' }}
                    >
                        Changing a button from blue to green isn&apos;t a conversion strategy. Real CRO asks bigger questions — and follows them all the way to an answer.
                    </motion.p>
                </div>

                <div className="lg:col-span-7">
                    <QuestionFlow />
                </div>
            </div>
        </section>
    );
}
